import './AnnotationToolsList.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faBars, faEdit, faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { EditIcon } from '../../../../../../design/static/icons/EditIcon';
import { GTable } from '../../../../../components/table/GTable';
import { IGColumnProps } from '../../../../../components/table/IGColumnProps';
import { COMMA_SEPARATOR } from '../../../../../helpers/global.constants';
import { ImageScopeName } from '../../../../annotation/question.model';
import { useProjectDetailsPermissions } from '../../../projectDetails.permissions';
import { IProjectDetailsStore } from '../../../projectDetails.store';
import { ProjectAttributeFormContainer } from '../containers/CreateAttribute.container';
import { EditQuestionContainer } from '../containers/EditQuestion.container';
import { QuestionDeleteContainer } from '../containers/QuestionDeleteContainer';
import { IAnnotationType, IQuestion } from '../projectDetailsTools.models';

library.add(faEdit, faEye, faBars);

type IQuestionWithScopes = IQuestion & { scopesIds: string[] };

interface IAttributesListProps {
  questions: IQuestion[];
  usedTools: string[];
  markingTools: IAnnotationType[];
  isLoading: boolean;
  onOrderChange: (questionId: string, to: number, isAttribute: boolean) => Promise<void>;
}

export const AttributesList = observer((props: IAttributesListProps): ReactElement => {
  const projectDetailsPermissions = useProjectDetailsPermissions();
  const projectDetailsStore = useInjection(IProjectDetailsStore.$);

  const { t } = useTranslator();
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [questionId, setQuestionId] = useState<string | undefined>('');
  const [isReadonly, setIsReadonly] = useState<boolean>(true);
  const canEdit = projectDetailsPermissions.canEditProject(projectDetailsStore.status);

  const getColumns = (): IGColumnProps<IQuestionWithScopes>[] => {
    const columns: IGColumnProps<IQuestionWithScopes>[] = [
      {
        sortBy: 'questionText',
        headerName: t('name'),
        renderer: (x) => x.questionText,
      },
      {
        sortBy: 'type',
        headerName: t('attribute_type'),
        renderer: typeRenderer,
      },
      {
        sortBy: 'answers',
        headerName: t('answers'),
        renderer: answersRenderer,
        cellClassName: 'hide-cell-overflow',
      },
      {
        sortBy: 'scopes',
        headerName: t('scope'),
        renderer: scopesRenderer,
        cellClassName: 'hide-cell-overflow',
      },
      {
        sortBy: 'isRequired',
        headerName: t('is_required'),
        renderer: requiredRenderer,
      },
      {
        headerName: t('actions'),
        actions: true,
        width: 'min-content',
        renderer: optionsRenderer,
      },
    ];

    if (canEdit.allowed) {
      columns.unshift({
        headerName: '',
        isDragHandle: true,
        renderer: (): ReactElement => <FontAwesomeIcon icon={faBars} />,
      });
    }

    return columns;
  };

  const typeRenderer = (data: IQuestion): ReactElement => <span>{t(`${data.type}_question_type`)}</span>;
  const requiredRenderer = (data: IQuestion): ReactElement => <span>{t(data.isRequired ? 'yes' : 'no')}</span>;
  const answersRenderer = (data: IQuestion): ReactElement => {
    const text = data.answers.map((x) => x.text).join(COMMA_SEPARATOR);
    return <span title={text}>{text}</span>;
  };
  const scopesRenderer = (data: IQuestion): ReactElement => <span title={data.scopes.join(COMMA_SEPARATOR)}>{data.scopes.join(COMMA_SEPARATOR)}</span>;
  const optionsRenderer = (data: IQuestionWithScopes): ReactElement => {
    return (
      <>
        {canEdit.allowed ? (
          <IconButton
            size='sm'
            color='primary'
            onClick={(): void => {
              showEditModalHandler({ id: data.id, isReadonly: false });
            }}
            title={t('edit_attribute')}
          >
            <EditIcon />
          </IconButton>
        ) : (
          <IconButton
            size='sm'
            onClick={(): void => {
              showEditModalHandler({ id: data.id, isReadonly: true });
            }}
            title={t('view_attribute')}
          >
            <FontAwesomeIcon icon={faEye} />
          </IconButton>
        )}

        <QuestionDeleteContainer
          question={data}
          isAttribute={true}
        />
      </>
    );
  };

  const showEditModalHandler = (data: { id?: string; isReadonly: boolean }): void => {
    setShowEditModal(true);
    setQuestionId(data.id);
    setIsReadonly(data.isReadonly);
  };

  const hideEditModal = (): void => {
    setShowEditModal(false);
  };

  const handleRowDrag =
    (cb: (questionId: string, to: number, isAttribute: boolean) => Promise<void>) =>
    async (from: number, to: number): Promise<void> => {
      await cb(props.questions[from].id, to, true);
    };

  const questions = props.questions.map((q) => ({
    ...q,
    scopes: q.scopes.map((s) =>
      s.toLocaleUpperCase() === ImageScopeName.toUpperCase() ? s : (props.markingTools.find((mt) => mt.id.toUpperCase() === s.toUpperCase())?.name ?? '')
    ),
    scopesIds: q.scopes,
  }));

  return (
    <div className='annotation-tools-list'>
      <div className='annotation-tools-list-header'>
        <div>{t('attributes')}</div>
        <div>{canEdit.allowed && <ProjectAttributeFormContainer />}</div>
      </div>
      <GTable
        isLoading={props.isLoading}
        noItemsPlaceholder={t('no_attributes_have_been_added_yet')}
        columns={getColumns()}
        items={questions}
        enableDrag={canEdit.allowed}
        onRowDragEnd={handleRowDrag(props.onOrderChange)}
      />
      {questionId && (
        <EditQuestionContainer
          questionId={questionId}
          onSubmit={hideEditModal}
          onCancel={hideEditModal}
          isReadonly={isReadonly}
          isAttribute={true}
          showEditModal={showEditModal}
          modalHeader={isReadonly ? t('view_attribute') : t('edit_attribute')}
        />
      )}
    </div>
  );
});
